import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { cross, type Item } from '@qoobd/rules';
import { game, PLAYER, RIVAL, ROOM_SIZE, WATCH_NAMES, roomCenter, type Pose } from '../game/controller';
import { useGame } from '../game/store';
import { buildRooms, panelTexture, shutterTexture, ROOM_LAYERS, type RoomLayer } from './rooms';
import { Armor } from './Armor';
import { Caches, Seals } from './Props';

/**
 * AI vs AI spectator dolly. The camera rides beside the watched agent, outside its room, with the
 * near wall cut away. Zoomed in it shows that one room; zoomed out it shows the 3×3 layer the
 * agent is in, plus faint ghost layers in front and behind. By default the view keeps a fixed
 * world orientation; with auto-reorient it turns to keep the watched agent's "up" at the top.
 */

const H = ROOM_SIZE / 2;
const J = 0.18;
const COLOR: Record<string, number> = { [PLAYER]: 0xffb000, [RIVAL]: 0xa9c4db };
const FIXED_VIEW = new THREE.Vector3(0.38, 0.32, 1).normalize(); // a little above and to the side
const WORLD_UP = new THREE.Vector3(0, 1, 0);

function label(text: string, color: string): THREE.SpriteMaterial {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 96;
  const g = c.getContext('2d')!;
  g.fillStyle = 'rgba(22,25,30,0.82)';
  g.fillRect(0, 0, 512, 96);
  g.strokeStyle = color;
  g.lineWidth = 4;
  g.strokeRect(2, 2, 508, 92);
  g.fillStyle = color;
  g.font = '600 46px "IBM Plex Mono", ui-monospace, monospace';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, 256, 50);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.SpriteMaterial({ map: tex, depthTest: false, transparent: true });
}

function Agent({ id }: { id: string }) {
  const armor = useGame((s) => (id === PLAYER ? s.watchArmor : s.rivalArmor));
  const group = useRef<THREE.Group>(null);
  const body = useRef<THREE.Group>(null);
  const light = useRef<THREE.PointLight>(null);
  const sprite = useRef<THREE.Sprite>(null);
  const pose = useMemo<Pose>(() => ({ position: new THREE.Vector3(), quaternion: new THREE.Quaternion() }), []);
  const hex = `#${COLOR[id]!.toString(16).padStart(6, '0')}`;
  const labels = useMemo(() => {
    const out = {} as Record<Item, THREE.SpriteMaterial>;
    for (const item of ['rock', 'paper', 'scissors'] as Item[]) out[item] = label(`${WATCH_NAMES[id]} · ${item.toUpperCase()}`, hex);
    return out;
  }, [id, hex]);
  const up = useMemo(() => new THREE.Vector3(), []);
  useFrame(() => {
    const m = game.match;
    if (!m || !group.current || !body.current || !sprite.current) return;
    game.pose(id, pose);
    group.current.position.copy(pose.position);
    body.current.quaternion.copy(pose.quaternion);
    up.set(0, 1, 0).applyQuaternion(pose.quaternion);
    sprite.current.position.copy(up).multiplyScalar(1.25);
    const item = m.agent(id)!.item;
    if (sprite.current.material !== labels[item]) sprite.current.material = labels[item];
  });
  return (
    <group ref={group}>
      <group ref={body}>
        <Armor armor={armor} side={COLOR[id]!} />
      </group>
      <pointLight ref={light} color={COLOR[id]} intensity={6} distance={4} decay={2} />
      <sprite ref={sprite} scale={[1.9, 0.36, 1]} renderOrder={10} material={labels.rock} />
    </group>
  );
}

/** Each agent's recent path through room centres. */
function Trails() {
  const show = useGame((s) => s.watch.trails);
  const lines = useMemo(
    () =>
      [PLAYER, RIVAL].map((id) => {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(32 * 3), 3));
        const mat = new THREE.LineBasicMaterial({ color: COLOR[id], transparent: true, opacity: 0.75, depthTest: false });
        const line = new THREE.Line(geo, mat);
        line.renderOrder = 8;
        return { id, line, count: -1 };
      }),
    [],
  );
  const v = useMemo(() => new THREE.Vector3(), []);
  useFrame(() => {
    const m = game.match;
    if (!m) return;
    for (const l of lines) {
      const path = l.id === PLAYER ? game.path : game.rivalPath;
      if (path.length === l.count) continue;
      l.count = path.length;
      const recent = path.slice(-12);
      const attr = l.line.geometry.getAttribute('position') as THREE.BufferAttribute;
      const off = l.id === PLAYER ? -0.12 : 0.12;
      recent.forEach((room, i) => {
        roomCenter(m.maze, room, v);
        attr.setXYZ(i, v.x + off, v.y + off - 1.4, v.z + off);
      });
      attr.needsUpdate = true;
      l.line.geometry.setDrawRange(0, recent.length);
    }
  });
  return <>{show && lines.map((l) => <primitive key={l.id} object={l.line} />)}</>;
}

export function SpectatorScene() {
  const { camera, gl, scene } = useThree();
  const sceneVersion = useGame((s) => s.sceneVersion);
  const zoom = useGame((s) => s.watch.zoom);
  const followId = useGame((s) => s.watch.follow);
  const core = useRef<THREE.Group>(null);

  useEffect(() => {
    scene.background = new THREE.Color(0x0e1013);
    scene.fog = new THREE.FogExp2(0x0e1013, 0.018);
    gl.localClippingEnabled = true;
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = 1.2;
    const cam = camera as THREE.PerspectiveCamera;
    const fov = cam.fov;
    cam.fov = 50;
    cam.far = 120;
    cam.updateProjectionMatrix();
    return () => {
      cam.fov = fov;
      cam.updateProjectionMatrix();
      gl.localClippingEnabled = false;
    };
  }, [camera, gl, scene]);

  // Near plane removes the walls between the camera and the watched layer; far plane removes
  // everything behind it. Ghost copies draw the rest faintly.
  const planes = useMemo(() => ({ near: new THREE.Plane(), far: new THREE.Plane() }), []);
  const materials = useMemo(() => {
    const tex = panelTexture(gl.capabilities.getMaxAnisotropy());
    const clip = [planes.near, planes.far];
    const solid: Record<RoomLayer, THREE.Material> = {
      wall: new THREE.MeshStandardMaterial({ map: tex, roughness: 0.6, metalness: 0.35, side: THREE.DoubleSide, vertexColors: true, clippingPlanes: clip }),
      jamb: new THREE.MeshStandardMaterial({ color: 0x6f757e, roughness: 0.45, metalness: 0.55, side: THREE.DoubleSide, clippingPlanes: clip }),
      trail: new THREE.MeshBasicMaterial({ color: 0x4f555e, side: THREE.DoubleSide, clippingPlanes: clip }),
      idle: new THREE.MeshBasicMaterial({ color: 0x4f555e, side: THREE.DoubleSide, clippingPlanes: clip }),
      bars: new THREE.MeshBasicMaterial({ color: 0xcfc8ba, side: THREE.DoubleSide, clippingPlanes: clip }),
      oneway: new THREE.MeshBasicMaterial({ map: shutterTexture(), transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false, clippingPlanes: clip }),
    };
    // Ghost layers are glass outlines: just the edges of the walls in front and behind.
    const ghost = new THREE.LineBasicMaterial({ color: 0x8b929c, transparent: true, opacity: 0.16, depthWrite: false });
    return { solid, ghost };
  }, [gl, planes]);

  const geometries = useMemo(() => {
    const m = game.match;
    return m ? buildRooms(m.maze, game.visible, new Set()) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneVersion]);
  const edges = useMemo(() => (geometries && zoom === 'out' ? new THREE.EdgesGeometry(geometries.wall, 30) : null), [geometries, zoom]);
  useEffect(
    () => () => {
      if (geometries) Object.values(geometries).forEach((g) => g.dispose());
      edges?.dispose();
    },
    [geometries, edges],
  );
  useEffect(() => game.refocus(), [zoom, followId]);

  const t = useMemo(
    () => ({
      pose: { position: new THREE.Vector3(), quaternion: new THREE.Quaternion() } as Pose,
      view: new THREE.Vector3().copy(FIXED_VIEW),
      up: new THREE.Vector3(0, 1, 0),
      want: new THREE.Vector3(),
      wantUp: new THREE.Vector3(),
      centre: new THREE.Vector3(),
      m4: new THREE.Matrix4(),
      q: new THREE.Quaternion(),
    }),
    [],
  );

  useFrame((_, dt) => {
    const m = game.match;
    if (!m) return;
    game.tick();
    const s = useGame.getState();
    const id = s.watch.follow;
    game.pose(id, t.pose);
    const agent = m.agent(id)!;

    // Where the camera wants to look from: fixed world view, or beside the agent's own right side.
    if (s.watch.reorient) {
      const o = agent.orientation;
      const r = cross(o.facing, o.up);
      t.want.set(r[0], r[1], r[2]).multiplyScalar(-1).addScaledVector(new THREE.Vector3(o.up[0], o.up[1], o.up[2]), 0.35).addScaledVector(new THREE.Vector3(-o.facing[0], -o.facing[1], -o.facing[2]), 0.3).normalize();
      t.wantUp.set(o.up[0], o.up[1], o.up[2]);
    } else {
      t.want.copy(FIXED_VIEW);
      t.wantUp.copy(WORLD_UP);
    }
    const k = 1 - Math.exp(-dt * 4);
    t.view.lerp(t.want, k).normalize();
    t.up.lerp(t.wantUp, k).normalize();

    const dist = s.watch.zoom === 'in' ? 7 : 17;
    camera.position.copy(t.pose.position).addScaledVector(t.view, dist);
    camera.up.copy(t.up);
    camera.lookAt(t.pose.position);

    // Cut planes follow the agent's room (not its in-between position) so walls never flicker.
    // They are square to the cube, along the world axis the camera looks most nearly down.
    roomCenter(m.maze, agent.room, t.centre);
    const ax = [Math.abs(t.view.x), Math.abs(t.view.y), Math.abs(t.view.z)];
    const i = ax.indexOf(Math.max(...ax));
    const n = t.want.set(0, 0, 0).setComponent(i, Math.sign(t.view.getComponent(i)) || 1);
    const d = n.dot(t.centre);
    planes.near.set(n.clone().negate(), d + H - J - 0.02);
    planes.far.set(n.clone(), -d + H + J + 0.02);

    if (core.current) {
      core.current.visible = game.visible.has(m.maze.core);
      roomCenter(m.maze, m.maze.core, core.current.position);
      core.current.rotation.y += dt * 0.8;
    }
  });

  return (
    <>
      <ambientLight color={0x9aa3b0} intensity={0.8} />
      <hemisphereLight args={[0xe8e3d9, 0x16191e, 0.45]} />
      <directionalLight position={[20, 30, 25]} intensity={0.7} />
      {geometries &&
        ROOM_LAYERS.map((layer) => <mesh key={`${layer}-${sceneVersion}`} geometry={geometries[layer]} material={materials.solid[layer]} />)}
      {edges && <lineSegments key={`ghost-${sceneVersion}`} geometry={edges} material={materials.ghost} renderOrder={2} />}
      <group ref={core}>
        <mesh>
          <octahedronGeometry args={[0.42, 0]} />
          <meshStandardMaterial color={0x3fd0d4} emissive={0x3fd0d4} emissiveIntensity={1.4} />
        </mesh>
        <pointLight color={0x3fd0d4} intensity={10} distance={5} decay={2} />
      </group>
      <Agent id={PLAYER} />
      <Agent id={RIVAL} />
      <Trails />
      <Seals all />
      <Caches all />
    </>
  );
}
